import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { IconFontType } from '../../shared/icon-font/icon-font-type.enum';
import { InstrumentationData } from '../../shared/instrumentation/instrumentation-data.model';
import { InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FileItem, FileType } from '../../shared/models/file-item.model';
import { EventService } from '../../shared/services/event.service';
import { MetaTransformCapacityService } from '../../shared/services/meta-transform-capacity.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { GuidGenerator } from '../../shared/utilities/guid-generator.utility';
import { UploadSource } from '../models/upload-session.model';
import { UploadConfigService } from '../upload-config.service';
import { UploadButtonService } from './upload-button.service';

import './upload-button.component.css';

@Component({
    selector: 'ohp-upload-button',
    templateUrl: './upload-button.component.html'
})
export class UploadButtonComponent implements OnInit {

    public ariaLabel: string;
    public clickData: InstrumentationData;
    public icon: IconFontType;
    public show: boolean;

    @Input() public buttonOrigin: string;

    @Input() public showAsLink: string;

    @Input() public fileType: FileType;

    @Input() public title?: string;

    @Input() public set numDocuments(numDocuments: number) {
        this._numDocuments = numDocuments;
        this.updateNextSessionId();
    }

    @ViewChild('uploadButton') public uploadButton: ElementRef;

    private currentUploadSessionId: string;
    private nextUploadSessionId: string;
    private _numDocuments: number;
    private defaultExtensions: Array<string>;
    private updateDefaultExtensionsPromise: Promise<Array<string>>;

    constructor(
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly uploadButtonService: UploadButtonService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly metaTransformCapacityService: MetaTransformCapacityService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler
    ) {
    }

    public ngOnInit() {
        if (!this.uploadConfigService.config) {
            return;
        }
        this.show = true;
        const resources = this.uploadConfigService.config.commonResources;
        this.title = this.title || resources.uploadButtonTitle;
        this.ariaLabel = resources.uploadButtonAriaLabel;
        this.icon = this.uploadConfigService.config.folderIconEnabled ? IconFontType.FolderOpen : IconFontType.Upload;

        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.UploadButtonImpression);

        this.eventService.broadcastEvent({
            name: AngularEvents.uploadButtonResponse,
            args: []
        });

        if (this.capabilitiesService.capabilities.filePicker) {
            this.clickData = {
                eventName: KnownInstrumentationEvents.UploadFilePickerClick
            };
            return;
        }

        this.getDefaultExtensions();
        this.updateNextSessionId();
    }

    public selectFile() {
        if (this.capabilitiesService.capabilities.filePicker) {
            this.eventService.broadcastEvent({
                name: AngularEvents.filePicker,
                args: [true]
            });
        } else {
            this.eventService.broadcastEvent({
                name: AngularEvents.uploadButtonClick,
                args: []
            });
            this.currentUploadSessionId = this.nextUploadSessionId;
            this.updateNextSessionId();
            this.getDefaultExtensions()
                .then(extensions => {
                    this.uploadButtonService.startUploadFlow(extensions, this.currentUploadSessionId, this.uploadButton, 'upload-select-file', UploadSource.Button);
                });
        }
    }

    public getDefaultExtensions(): Promise<Array<string>> {
        if (this.updateDefaultExtensionsPromise) {
            return this.updateDefaultExtensionsPromise;
        }

        this.updateDefaultExtensionsPromise = new Promise<Array<string>>(resolve => {
            if (this.capabilitiesService.capabilities.metaExtensionsFetchEnabled) {
                this.metaTransformCapacityService.getVideoExtensions()
                    .then(data => {
                        FileItem.setStreamExtensions(data);
                    })
                    .then(() => {
                        this.updateDefaultExtensions();
                        resolve(this.defaultExtensions);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.ouSq3);
                        this.updateDefaultExtensions();
                        resolve(this.defaultExtensions);
                    });
            } else {
                this.updateDefaultExtensions();
                resolve(this.defaultExtensions);
            }
        });

        return this.updateDefaultExtensionsPromise;
    }

    private updateNextSessionId() {
        this.nextUploadSessionId = GuidGenerator.createGuid();
        this.clickData = this.uploadButtonService.getClickDataForId('upload-start', this.nextUploadSessionId, UploadSource.Button, this.buttonOrigin, this._numDocuments);
    }

    private updateDefaultExtensions() {
        this.defaultExtensions = [];
        const fileTypes = this.fileType ? new Set([this.fileType]) : this.uploadConfigService.config.uploadFileTypes;
        fileTypes.forEach(fileType => {
            const extensions = FileItem.fileExtensionsForType(fileType).map(ext => `.${ext}`);
            if (extensions.length) {
                this.defaultExtensions.push(...extensions);
            }
        });
    }
}
